import { formatDate } from '../../lib/utils';
import {
	useGetAllOrganizationIndustriesAdminQuery,
	useCreateOrganizationIndustryMutation,
	useUpdateOrganizationIndustryMutation,
	usePublishOrganizationIndustryMutation,
	useDeleteOrganizationIndustryMutation,
} from '@codezo/graphql';
import { StatusBadge } from '../../components/ui/status-badge';
import { MoreHorizontal, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';

/* ── Create/Edit modal ── */

function IndustryModal({
	open,
	onOpenChange,
	mode,
	initialName,
	initialPublished,
	onSubmit,
	loading,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	mode: 'create' | 'edit';
	initialName?: string;
	initialPublished?: boolean;
	onSubmit: (name: string, isPublished: boolean) => void;
	loading: boolean;
}) {
	const [name, setName] = useState(initialName || '');
	const [isPublished, setIsPublished] = useState(initialPublished ?? true);

	useEffect(() => {
		if (open) {
			setName(initialName || '');
			setIsPublished(initialPublished ?? true);
		}
	}, [open, initialName, initialPublished]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>
						{mode === 'create' ? 'Add industry' : 'Edit industry'}
					</DialogTitle>
					<DialogDescription>
						{mode === 'create'
							? 'Create a new industry for organizations.'
							: 'Update this industry.'}
					</DialogDescription>
				</DialogHeader>
				<DialogBody>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label>
								Name <span className="text-destructive">*</span>
							</Label>
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Automotive"
								maxLength={255}
							/>
						</div>
						{mode === 'edit' && (
							<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
								<div>
									<Label className="text-sm">Published</Label>
									<p className="text-sm text-muted-foreground">
										Make this industry visible to all clients
									</p>
								</div>
								<Switch checked={isPublished} onCheckedChange={setIsPublished} />
							</div>
						)}
					</div>
				</DialogBody>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						onClick={() => onSubmit(name.trim(), isPublished)}
						disabled={!name.trim() || loading}
					>
						{loading
							? mode === 'create'
								? 'Creating…'
								: 'Saving…'
							: mode === 'create'
								? 'Create industry'
								: 'Save changes'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/* ════════════════════════ INDUSTRIES TAB ════════════════════════ */

export function IndustriesTab() {
	const { data, loading, refetch } = useGetAllOrganizationIndustriesAdminQuery({
		fetchPolicy: 'no-cache',
	});
	const [createIndustry, { loading: creating }] = useCreateOrganizationIndustryMutation();
	const [updateIndustry, { loading: updating }] = useUpdateOrganizationIndustryMutation();
	const [publishIndustry] = usePublishOrganizationIndustryMutation();
	const [deleteIndustry, { loading: deleting }] = useDeleteOrganizationIndustryMutation();

	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<{
		id: string;
		name: string;
		isPublished: boolean;
	} | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

	const industries = data?.getAllOrganizationIndustriesAdmin ?? [];

	const handleCreate = async (name: string) => {
		try {
			await createIndustry({ variables: { input: { name } } });
			toast.success('Industry created');
			setCreateOpen(false);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create industry');
		}
	};

	const handleUpdate = async (name: string, isPublished: boolean) => {
		if (!editTarget) return;
		try {
			await updateIndustry({
				variables: {
					input: {
						id: editTarget.id,
						...(name !== editTarget.name ? { name } : {}),
						...(isPublished !== editTarget.isPublished ? { isPublished } : {}),
					},
				},
			});
			toast.success('Industry updated');
			setEditTarget(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update industry');
		}
	};

	const handlePublish = async (id: string) => {
		try {
			await publishIndustry({ variables: { id } });
			toast.success('Industry published');
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to publish industry');
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteIndustry({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Industry deleted');
			setDeleteTarget(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete industry');
		}
	};

	if (loading) {
		return (
			<Card className="border-line-1 bg-surface gap-0 py-0">
				<div className="space-y-3 p-6">
					{Array.from({ length: 5 }).map((_, i) => (
						<div key={i} className="h-10 animate-pulse rounded bg-surface-fill" />
					))}
				</div>
			</Card>
		);
	}

	return (
		<>
			<div className="mb-4 flex items-center justify-between">
				<p className="text-base text-text-3">{industries.length} industries</p>
				<Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
					<Plus size={14} data-icon="inline-start" />
					Add industry
				</Button>
			</div>

			<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
				<div className="overflow-x-auto">
					<Table className="min-w-[500px]">
						<TableHeader>
							<TableRow className="border-line-1 hover:bg-transparent">
								<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Name
								</TableHead>
								<TableHead className="w-[100px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Status
								</TableHead>
								<TableHead className="w-[120px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Source
								</TableHead>
								<TableHead className="w-[120px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Created
								</TableHead>
								<TableHead className="w-10" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{industries.map((ind) => (
								<TableRow
									key={ind.id}
									className="border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
								>
									<TableCell className="text-base font-medium text-text-1">
										{ind.name}
									</TableCell>
									<TableCell>
										<StatusBadge
											active={ind.isPublished}
											activeLabel="Published"
											inactiveLabel="Pending"
											inactiveTone="warn"
										/>
									</TableCell>
									<TableCell className="text-base text-text-2">
										{ind.createdByOrganizationId ? 'Client' : 'Admin'}
									</TableCell>
									<TableCell className="text-base text-text-3">
										{ind.createdAt ? formatDate(ind.createdAt) : '—'}
									</TableCell>
									<TableCell>
										<div className="flex justify-end">
											<DropdownMenu>
												<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
													<MoreHorizontal size={14} />
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													{!ind.isPublished && (
														<DropdownMenuItem
															onClick={() => handlePublish(ind.id)}
														>
															<Upload size={14} /> Publish
														</DropdownMenuItem>
													)}
													<DropdownMenuItem
														onClick={() =>
															setEditTarget({
																id: ind.id,
																name: ind.name,
																isPublished: ind.isPublished,
															})
														}
													>
														<Pencil size={14} /> Edit
													</DropdownMenuItem>
													<DropdownMenuItem
														variant="destructive"
														onClick={() =>
															setDeleteTarget({
																id: ind.id,
																name: ind.name,
															})
														}
													>
														<Trash2 size={14} /> Delete
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</Card>

			{/* Create modal */}
			<IndustryModal
				open={createOpen}
				onOpenChange={setCreateOpen}
				mode="create"
				onSubmit={(name) => handleCreate(name)}
				loading={creating}
			/>

			{/* Edit modal */}
			<IndustryModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				mode="edit"
				initialName={editTarget?.name}
				initialPublished={editTarget?.isPublished}
				onSubmit={handleUpdate}
				loading={updating}
			/>

			{/* Delete confirm */}
			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Delete industry"
				description={`Are you sure you want to delete "${deleteTarget?.name}"? Organizations using this industry will have it cleared.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>
		</>
	);
}
